/*
 * Copyright 2025 Intuition Robotics ltd
 */
import __asset_0 from "@res/icons/btn_toggle_on_outline.svg";
import __asset_1 from "@res/icons/btn_toggle_off_outline.svg";
import * as React from "react";
import {useContext, useEffect} from "react";
import * as emotion from "emotion";
import {BaseComponent, XhrHttpModule} from "@intuitionrobotics/thunderstorm/frontend";
import {HttpMethod} from "@intuitionrobotics/thunderstorm";
import {DB_Patient} from "@app/app-shared/patient";
import {MedicalScreening, MedicalScreeningDataResponse, MedicalScreeningPeriod, ScreeningStatus} from "@app/app-shared/medical-screening";
import {Api_GetMedicalScreenings, Api_SetScreeningEnabled} from "@app/app-shared/api";
import {Api_GetPcpContact} from "@app/ir-q-app-common/types/db-contact";
import {useSelectedPatient} from "./PatientProvider";
import {PatientsContext} from "./ui/patients/PatientsContext";
import {OrganizationsContext} from "./ui/organizations/OrganizationsContext";
import {navigateToPatientPage} from "@modules/PatientDataModule";
import {demoScreenings, demoPeriods} from "./hedis-demo-data";
import {padding_rl_sm, Spacing} from "@styles/styles";
import {COLORS} from "@res/colors";
import {FONTS} from "@res/fonts";
import {Loader} from "../../widgets/Loader";
import {Card} from "./widget/Card";
import {HLine} from "./widget/HLine";
import {VGap} from "./widget/VGap";
import {check_green_icon} from "@res/icons";
import {AnalyticConsts, AnalyticsModule} from "@modules/AnalyticsModule";
import {hideTooltip, showTooltip} from "./widget/appTooltip";

export const Page_Hedis = () => {
    const selectedPatient = useSelectedPatient();
    const {demo} = useContext(PatientsContext);
    const {organizations} = useContext(OrganizationsContext);

    // HEDIS is a MetroPlus-only tab — same rule that PatientTab uses to show/hide it.
    // Reached by direct URL for any other org, bounce back to the patient's dashboard
    // instead of rendering (its screening data was never produced, so formatHistoryData
    // throws and the tab white-screens). Demo data is self-contained, so always allow it.
    const org = selectedPatient ? organizations[selectedPatient.organizationId] : undefined;
    const orgsLoaded = Object.keys(organizations).length > 0;
    const hedisEnabled = org?.name?.toLowerCase()?.includes("metroplus") ?? false;
    const blocked = !demo && orgsLoaded && !hedisEnabled;

    useEffect(() => {
        if (selectedPatient && blocked)
            navigateToPatientPage(selectedPatient);
    }, [selectedPatient, blocked]);

    if (!selectedPatient)
        return null;
    if (demo)
        return <HedisContent patient={selectedPatient} demo={demo}/>;
    if (!orgsLoaded)
        return <Loader/>;          // wait for org info before deciding (avoids a bootstrap-race redirect)
    if (!hedisEnabled)
        return null;               // redirect fires from the effect above
    return <HedisContent patient={selectedPatient} demo={demo}/>;
};

const on = __asset_0;
const off = __asset_1;

const itemWrapper = emotion.css(
    {
        boxSizing: "border-box",
        paddingTop: 16,
        paddingBottom: 16,
    }
);

const toggleStyle = emotion.css(
    {
        marginRight: Spacing.m
    }
);

const statusLabels: Record<ScreeningStatus, string> = {
    [ScreeningStatus.PROPOSED]: "Suggested",
    [ScreeningStatus.REJECTED]: "Rejected",
    [ScreeningStatus.DONE_BEFORE]: "Done Before",
    [ScreeningStatus.CALLING_PCP]: "Tried to call PCP",
    [ScreeningStatus.MADE_APPOINTMENT]: "Made Appointment",
    [ScreeningStatus.SCHEDULED_TRANSPORT]: "Scheduled Transport",
    [ScreeningStatus.WAITING_FOR_APPOINTMENT]: "Waiting for Appointment",
    [ScreeningStatus.WENT_TO_APPOINTMENT]: "Went to Appointment",
};

type Props = {
    patient: DB_Patient;
    demo: boolean;
};

type FormatedHistoryEntry = {
    date: Date;
    doneDate?: Date | null;
    names: string[];
    status: ScreeningStatus;
};

type State = {
    screenings: MedicalScreening[] | null;
    periods: MedicalScreeningPeriod[] | null;
    history: FormatedHistoryEntry[] | null;
    loading: boolean;
    hasPcp: boolean | null;
};

const dot = emotion.css(
    {
        width: 4,
        height: 4,
        backgroundColor: COLORS.veryLightPink,
        borderRadius: "50%"
    }
);

class HedisContent extends BaseComponent<Props, State> {

    constructor(props: Props) {
        super(props);
        this.state = {
            screenings: null,
            periods: null,
            history: null,
            loading: true,
            hasPcp: null,
        };
    }

    componentDidMount() {
        if (this.props.demo)
            this.loadMockData();
        else {
            this.fetchScreenings();
            this.fetchPcpContact();
        }
    }

    componentDidUpdate(prevProps: Props) {
        const patientChanged = prevProps.patient.unitId !== this.props.patient.unitId;
        const demoChanged = prevProps.demo !== this.props.demo;
        if (!patientChanged && !demoChanged)
            return;

        if (this.props.demo)
            this.loadMockData();
        else {
            this.fetchScreenings();
            this.fetchPcpContact();
        }
    }

    private loadMockData = () => {
        const history = this.formatHistoryData(demoScreenings, demoPeriods);
        this.setState({screenings: demoScreenings, periods: demoPeriods, history, loading: false, hasPcp: true});
    };

    private fetchPcpContact = () => {
        const agentId = this.props.patient.unitId;
        XhrHttpModule
            .createRequest<Api_GetPcpContact>(HttpMethod.GET, "get-pcp-contact")
            .setRelativeUrl(`/v2/contacts/get-pcp?agentId=${agentId}`)
            .setOnError(() => this.setState({hasPcp: false}))
            .execute((contact) => this.setState({hasPcp: !!contact}));
    };

    private fetchScreenings = () => {
        const agentId = this.props.patient.unitId;
        this.setState({loading: true});
        XhrHttpModule
            .createRequest<Api_GetMedicalScreenings>(HttpMethod.GET, "get-medical-screenings")
            .setUrlParams({agentId})
            .setRelativeUrl("/v1/medical-screening/get")
            .setLabel("Fetching medical screenings")
            .setOnError(() => {
                this.setState({loading: false});
                return true;
            })
            .execute((data: MedicalScreeningDataResponse) => {
                this.setState({...data, loading: false, history: this.formatHistoryData(data.screenings, data.periods ?? [])});
            });
    };

    private setScreeningEnabled = (screeningId: number, enabled: boolean, screeningName: string) => {
        if (this.props.demo) {
            this.setState(prev => ({
                screenings: prev.screenings?.map(s =>
                    s.id === screeningId ? {...s, isEnabled: enabled} : s
                ) ?? null,
            }));
            return;
        }

        const agentId = this.props.patient.unitId;
        XhrHttpModule
            .createRequest<Api_SetScreeningEnabled>(HttpMethod.POST, "set-screening-enabled")
            .setRelativeUrl("/v1/medical-screening/set-enabled")
            .setLabel(`Setting screening ${screeningId} enabled=${enabled}`)
            .setJsonBody({agentId, screeningId, enabled})
            .setOnError(() => true)
            .execute(() => {
                this.setState(prev => ({
                    screenings: prev.screenings?.map(s =>
                        s.id === screeningId ? {...s, isEnabled: enabled} : s
                    ) ?? null,
                }));
            });
        AnalyticsModule.logCareManagerEvent({
            objectType: AnalyticConsts.ObjectTypes.HEDIS,
            entity: AnalyticConsts.Entities.USER,
            activityStatus: enabled ? "yes" : "no",
            objectSubType: AnalyticConsts.ObjectSubTypes.SCREENING_REQUEST,
            activityDetails: screeningName
                .toLowerCase()
                .replace(/[\s()]/g, ""),
            creator: this.props.patient.unitId,
        });
    };

    private renderScreeningItem = (screening: MedicalScreening) => {
        const isDisabled = this.state.hasPcp === false;
        return (
            <div className={`ll_h_c match_width ${itemWrapper}`}>
                <div className="ll_h_t" style={{flex: 1}}>
                    <div
                        className={`${toggleStyle} ${isDisabled ? '' : 'clickable'}`}
                        style={isDisabled ? {opacity: 0.4, cursor: 'not-allowed'} : undefined}
                        onMouseEnter={isDisabled ? (e) => showTooltip(
                            e,
                            "To enable HEDIS for this user, please add his PCP contact",
                            e.currentTarget.getBoundingClientRect().left,
                            e.currentTarget.getBoundingClientRect().bottom + 10
                        ) : undefined}
                        onMouseLeave={isDisabled ? () => hideTooltip() : undefined}
                        onClick={!isDisabled ? () => this.setScreeningEnabled(screening.id, !screening.isEnabled, screening.name) : undefined}
                    >
                        <div style={{width: 48, height: 24}}>
                            <img alt={screening.isEnabled ? "on" : "off"} src={screening.isEnabled ? on : off}/>
                        </div>
                    </div>
                    <div className="ll_v_s" style={{flex: 1}}>
                        {
                            screening.isEnabled
                                ? FONTS.reg_16_dark(screening.name)
                                : FONTS.reg_lightgray_16_L(screening.name)
                        }
                    </div>
                </div>
            </div>
        );
    };

    private formatHistoryData = (screenings: MedicalScreening[], periods: MedicalScreeningPeriod[]) : FormatedHistoryEntry[] => {
        const result: FormatedHistoryEntry[] = [];
        const alreadyReportedResults = new Set<string>();
        const periodsMap: Record<number, MedicalScreeningPeriod> = periods.reduce((map, period) => ({
            ...map,
            [period.id]: period,
        }), {});
        const screeningNames: Record<number, string> = screenings.reduce((map, screening) => ({
            ...map,
            [screening.id]: screening.name,
        }), {});
        for (const screening of screenings) {
            for (const status of screening.screeningStatuses ?? []) {
                switch (status.status) {
                    // these statuses we always show
                    case ScreeningStatus.REJECTED:
                    case ScreeningStatus.DONE_BEFORE:
                    case ScreeningStatus.PROPOSED:
                        result.push({
                            date: new Date(status.date_made_timestamp),
                            names: [screening.name],
                            status: status.status,
                            doneDate: status.appointment_date_timestamp ? new Date(status.appointment_date_timestamp) : null,
                        });
                        if (status.last_retry_timestamp != null) {
                            const lastRetryDate = new Date(status.last_retry_timestamp);
                            result.push({
                                date: lastRetryDate,
                                names: [screening.name],
                                status: status.status,
                            });
                        }
                        break;
                    default:
                        if (!status.screening_period_id) {
                            break;
                        }
                        const period = periodsMap[status.screening_period_id]
                        const key = `${period.id}-${status.status}-${status.date_made_timestamp}`;
                        if (alreadyReportedResults.has(key)) {
                            break;
                        }
                        alreadyReportedResults.add(key);
                        result.push({
                            date: new Date(status.date_made_timestamp),
                            names: period.all_screening_ids.map(id => screeningNames[id] ?? "Unknown screening"),
                            status: status.status,
                        });
                        if (status.last_retry_timestamp != null) {
                            const lastRetryDate = new Date(status.last_retry_timestamp);
                            result.push({
                                date: lastRetryDate,
                                names: period.all_screening_ids.map(id => screeningNames[id] ?? "Unknown screening"),
                                status: status.status,
                            });
                        }
                        break;
                }
            }
        }
        result.sort((a, b) => b.date.getTime() - a.date.getTime());
        return result;
    }

    private renderHistoryItemStatus = (status: ScreeningStatus, date: Date | null) => {
        const label = statusLabels[status];
        switch (status) {
            case ScreeningStatus.REJECTED:
                return (FONTS.bold_16_red(label));
            case ScreeningStatus.DONE_BEFORE:
                return (<>{!!date && FONTS.bold_16_gray(label)}{FONTS.reg_14_neutral_gray(date!.toLocaleDateString())}</>);
            case ScreeningStatus.WENT_TO_APPOINTMENT:
                return (<div style={{display: "flex", alignItems: "center", gap: 4}}><img alt={""} src={check_green_icon} height={12}/>{FONTS.bold_16_green(label)}</div>);
            default:
                return (FONTS.bold_16_orange_vivid(label));
        }
    }

    private renderHistoryItem = (historyEntry: FormatedHistoryEntry) => {
        return (
            <div className={`ll_v_s match_width ${itemWrapper}`} style={{display: "flex", flexDirection: "column", gap: 8}}>
                <div style={{display: "flex", alignItems: "center", gap: 8}}>
                    {FONTS.medium_16(historyEntry.date.toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric"
                    }))}
                    <div className={dot}/>
                    {this.renderHistoryItemStatus(historyEntry.status, historyEntry.doneDate ?? null)}
                </div>
                {historyEntry.names.map((name, i) => (
                    <div key={i} className="ll_v_s" style={{flex: 1}}>{FONTS.reg_14_gray(name)}</div>
                ))}
            </div>
        );
    };

    private renderScreeningsCard = (screenings: MedicalScreening[]) => {
        return (
            <Card style={{position: "relative", padding: `${Spacing.xs}px ${3 * Spacing.s}px`, flex: 1}}>
                <VGap v={Spacing.l}/>
                <div className={"ll_h_c"} style={{justifyContent: "space-between", height: "1.2em"}}>
                    {FONTS.reg_18("HEDIS Screening")}
                </div>
                <VGap v={Spacing.m}/>
                {screenings.map((screening, i) => (
                    <React.Fragment key={screening.id}>
                        {i !== 0 && <HLine style={{backgroundColor: COLORS.color_gray_medium}}/>}
                        {this.renderScreeningItem(screening)}
                    </React.Fragment>
                ))}
            </Card>
        );
    };

    private renderHistoryCard = (historyEntries: FormatedHistoryEntry[]) => {
        return (
            <Card style={{position: "relative", padding: `${Spacing.xs}px ${3 * Spacing.s}px`, flex: 1}}>
                <VGap v={Spacing.l}/>
                <div className={"ll_h_c"} style={{justifyContent: "space-between", height: "1.2em"}}>
                    {FONTS.reg_18("Screening history")}
                </div>
                <VGap v={Spacing.m}/>
                {historyEntries.length === 0
                    ? <div className={itemWrapper}>{FONTS.reg_14_secondary_dark("No history available")}</div>
                    : historyEntries.map((entry, i) => (
                        <React.Fragment key={i}>
                            {i !== 0 && <VGap v={Spacing.m}/>}
                            {this.renderHistoryItem(entry)}
                        </React.Fragment>
                    ))
                }
            </Card>
        );
    };

    render() {
        const {screenings, loading, history} = this.state;

        if (loading)
            return <Loader/>;

        if (!screenings)
            return null;

        if (!history)
            return null;

        return (
            <div className="ll_h_s" style={{marginLeft: -Spacing.s, marginRight: -Spacing.s, flex: 1}}>
                <div className={`ll_v_s ${padding_rl_sm}`} style={{flex: 2}}>
                    {this.renderScreeningsCard(screenings)}
                </div>
                <div className={`ll_v_s ${padding_rl_sm}`} style={{flex: 1}}>
                    {this.renderHistoryCard(history)}
                </div>
            </div>
        );
    }
}